import { makeAutoObservable } from "mobx";
import {
  HistoricalNodeResponseTimeData,
  Host,
  NodePartNetworking,
  NodeResponseTimes,
  PerNodeLatencyResponse,
} from "../apiResponses/influxResponses";
import {
  FullSegmentLatencyResponse,
  SegmentLatencyResponse,
} from "../apiResponses/realtimeResponse";
import { PROXY_HOST } from "../consts";
import AutoRequest from "../utils/autoRequest";
import { RootStore } from "./rootStore";

// InfluxStore stores information coming fairly directly from influx
export class InfluxStore {
  rootStore: RootStore;

  selectedHost: string | undefined;

  singleNodeResponseTimesReq: AutoRequest<HistoricalNodeResponseTimeData>;
  nodeResponseTimesReq: AutoRequest<NodeResponseTimes>;
  nodePartNetworkingReq: AutoRequest<NodePartNetworking>;
  hostListReq: AutoRequest<Host[]>;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    this.nodeResponseTimesReq = new AutoRequest(
      () => this.fetchNodeResponseTimes(),
      undefined,
      {
        debug: true,
        name: "NodeResponseTimes",
      },
    );

    this.singleNodeResponseTimesReq = new AutoRequest(
      () => this.fetchSingleNodeResponseTimes(),
      () => this.selectedHost !== undefined,
      {
        debug: true,
        name: "SingleNodeResponseTimes",
      },
    );

    this.hostListReq = new AutoRequest(() => this.fetchHosts(), undefined, {
      debug: true,
      name: "HostList",
    });

    this.nodePartNetworkingReq = new AutoRequest(
      () => this.fetchNodePartNetworking(),
      undefined,
      {
        debug: true,
        name: "NodePartNetworking",
      },
    );

    makeAutoObservable(this);
  }

  Initialize() {
    this.nodeResponseTimesReq.Initialize(() => [
      ...this.rootStore.dateStore.unixDateRange,
    ]);
    this.singleNodeResponseTimesReq.Initialize(() => [this.selectedHost]);
    this.nodePartNetworkingReq.Initialize(() => [
      ...this.rootStore.dateStore.unixDateRange,
    ]);
  }

  *fetchNodeResponseTimes(): any {
    const [start, end] = this.rootStore.dateStore.unixDateRange;
    let endpoint = `${PROXY_HOST}/node_stats/response_times?timestamp=${end}&comparison_timestamp=${start}`;
    // &timestamp=${end}&comparison_timestamp=${start}
    const req = this.nodeResponseTimesReq;

    try {
      const response = (yield fetch(endpoint, {
        headers: this.defaultHeaders,
      })) as Response;
      if (!response.ok) {
        req.reportError(response.status);
        return;
      }
      // TODO: what to do on 204?
      if (response.status !== 204) {
        const jsonData = (yield response.json()) as NodeResponseTimes;
        req.reportData(jsonData);
      } else {
        req.reportData({});
      }
    } catch (e) {
      req.reportError();
      return;
    }
  }

  *fetchSingleNodeResponseTimes(): any {
    const [start, end] = this.rootStore.dateStore.unixDateRange;
    let endpoint = `${PROXY_HOST}/node/${this.selectedHost}/response_times?start=${start}&end=${end}`;
    // &timestamp=${end}&comparison_timestamp=${start}
    const req = this.singleNodeResponseTimesReq;

    try {
      const response = (yield fetch(endpoint, {
        headers: this.defaultHeaders,
      })) as Response;
      if (!response.ok) {
        req.reportError(response.status);
        return;
      }
      // TODO: what to do on 204?
      if (response.status !== 204) {
        const jsonData =
          (yield response.json()) as HistoricalNodeResponseTimeData;
        jsonData.data.sort(
          (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
        );
        req.reportData(jsonData);
      } else {
        req.reportError();
      }
    } catch (e) {
      req.reportError();
      return;
    }
  }

  *fetchHosts(): any {
    let endpoint = `${PROXY_HOST}/hosts`;
    const req = this.hostListReq;

    try {
      const response = (yield fetch(endpoint, {
        headers: this.defaultHeaders,
      })) as Response;
      if (!response.ok) {
        req.reportError(response.status);
        return;
      }
      if (response.status !== 204) {
        const jsonData = (yield response.json()) as Host[];
        req.reportData(jsonData);
      } else {
        req.reportData([]);
      }
    } catch (e) {
      req.reportError();
      return;
    }
  }

  *fetchNodePartNetworking(): any {
    const [start, end] = this.rootStore.dateStore.unixDateRange;
    let endpoint = `${PROXY_HOST}/node_stats/part_networking?timestamp=${end}&comparison_timestamp=${start}`;
    const req = this.nodePartNetworkingReq;

    try {
      const response = (yield fetch(endpoint, {
        headers: this.defaultHeaders,
      })) as Response;
      if (!response.ok) {
        req.reportError(response.status);
        return;
      }
      // TODO: what to do on 204?
      if (response.status !== 204) {
        const jsonData = (yield response.json()) as NodePartNetworking;
        req.reportData(jsonData);
      } else {
        req.reportData({});
      }
    } catch (e) {
      req.reportError();
      return;
    }
  }

  get defaultHeaders(): HeadersInit {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.rootStore.googleAuthStore.credential}`,
    };
  }

  get allRequests(): AutoRequest<any>[] {
    return [
      this.nodeResponseTimesReq,
      this.nodePartNetworkingReq,
      this.hostListReq,
      this.singleNodeResponseTimesReq,
    ];
  }

  get hosts(): Host[] {
    return this.hostListReq.getData() ?? [];
  }

  refreshAll() {
    this.allRequests.forEach((req) => req.markInvalid());
  }
}
